import React, { useState } from 'react';
import { Row, Col, Tooltip, Grid, Skeleton } from 'antd';
import { t } from 'i18next';

import FlowIcon from '@/comp/common/icons/FlowIcon';
import { Compressor, TestParameter, TestState } from '@/store/InstallationValidation/constants';
import { useStores } from '@/hooks/use-stores';
import SensorWidgetWithIcon from '@/comp/common/icons/SensorWidgetWithIcon';

import {
  getFlowCommentButtonText,
  getStatusDisplay,
  getStyleType,
  GridFlowResponseProps,
  isNoPowerSensorVisible,
  shouldShowFlowCommentButton,
} from './FlowSection.utils';
import { CardsContainer, CardsRow, FlowGroupContainer } from './FlowSection.styles';
import TestStatusCard from '../TestStatusCard/TestStatusCard';
import MobileCommentDrawer from '../MobileCommentDrawer/MobileCommentDrawer';
import CommentsModal from '../CommentsModal/CommentsModal';

interface FlowSectionProps {
  sectionTitle: string;
  compressors: Compressor[];
  connectivityIconAndStatus: { icon: JSX.Element; status: string };
  flowSumStatus?: TestParameter;
  testState?: TestState;
  isLoading?: boolean;
}
const { useBreakpoint } = Grid;

const FlowSection: React.FC<FlowSectionProps> = ({
  sectionTitle,
  compressors,
  flowSumStatus,
  connectivityIconAndStatus,
  testState,
  isLoading,
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { installationValidationStore } = useStores();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const flowSumComment = installationValidationStore.getComment(flowSumStatus?.id)
    ? installationValidationStore.getComment(flowSumStatus?.id)
    : flowSumStatus?.comment;

  const hasComments = Boolean(flowSumComment);

  const handleCommentSave = (loadComment: string, unloadComment: string, flowComment: string) => {
    if (flowSumStatus?.id) {
      installationValidationStore.updateComment(flowSumStatus.id, flowComment);
    }
    setIsModalOpen(false);
  };

  const showCommentsButton = shouldShowFlowCommentButton(testState, flowSumStatus?.status);

  const GridFlowResponse = ({ estimatedFlow, sensorReadingFlow }: GridFlowResponseProps) => {
    const isValid = (val: number) => typeof val === 'number' && !isNaN(val);

    const renderValueWithUnit = (value: number, isValidValue: boolean) => (
      <>
        <span className="flow-specific-value">{isValidValue ? value.toString() : t('_ _')}</span>
        {isValidValue && <span className="flow-specific-type">{t('CFM')}</span>}
      </>
    );
    const percentageDiff = ((sensorReadingFlow - estimatedFlow) / estimatedFlow) * 100;

    const shouldShowEstimatedFlow = isValid(estimatedFlow);
    const shouldShowSensorReadingFlow = isValid(sensorReadingFlow);
    const shouldShowPercentageDiff = isValid(percentageDiff);

    return (
      <div className="flow-grid-container">
        <div className="mini-col">
          <div className="mini-cell text-cell">{t('ESTIMATED_FLOW')}</div>
          <div className="mini-cell">{renderValueWithUnit(estimatedFlow, shouldShowEstimatedFlow)}</div>
        </div>

        <div className="mini-col">
          <div className="mini-cell">{isMobile ? t('Sensor') : t('SENSOR_READING')}</div>
          <div className="mini-cell">{renderValueWithUnit(sensorReadingFlow, shouldShowSensorReadingFlow)}</div>
        </div>

        <div className="mini-col mini-col-margin">
          <div className="mini-cell">{t('Differences')}</div>
          <div
            className={`mini-cell flow-specific-value-differences ${
              shouldShowPercentageDiff ? 'flow-specific-value-differences-color' : 'flow-specific-value'
            }`}
          >
            {shouldShowPercentageDiff ? `${percentageDiff.toFixed(2)}%` : t('_ _')}
          </div>
        </div>

        <div className="mini-col mini-col-status mini-col-more-margin">
          <div className="mini-cell">{t('Flow sum')}</div>
          <span className={`flow-sum-value ${getStyleType(flowSumStatus.status)}`}>
            {getStatusDisplay(flowSumStatus.status, testState)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <FlowGroupContainer $screens={screens} $isMobile={isMobile} className="custom-style">
      <div className="bordered-container">
        <Skeleton
          active
          avatar={{ shape: 'circle', size: 'large' }}
          title={{ width: 90 }}
          paragraph={false}
          loading={isLoading}
          rootClassName="flow-section-skeleton"
        >
          <Row gutter={[0, 16]}>
            <Col span={24}>
              {sectionTitle && (
                <div className="flow-header-content">
                  <div className="flow-title-container">
                    <div className="flow-icon-wrapper">
                      <SensorWidgetWithIcon width={40} height={40} Icon={<FlowIcon />} />
                      <Tooltip title={sectionTitle}>
                        <h2 className="flow-title">{sectionTitle}</h2>
                      </Tooltip>
                    </div>
                    <div className="flow-comment-button-container">
                      <GridFlowResponse
                        estimatedFlow={compressors[0]?.FLOW?.expectValue}
                        sensorReadingFlow={compressors[0]?.FLOW?.realValue}
                      />
                      {testState !== TestState.INITIAL && showCommentsButton && (
                        <div className="flow-comment-button" onClick={() => setIsModalOpen(true)}>
                          <div className="flow-comment-button-text">
                            {getFlowCommentButtonText(testState, hasComments)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Skeleton>
        <CardsContainer>
          <CardsRow gutter={[16, 16]}>
            {compressors.map(compressor => (
              <div className="card-column" key={compressor.deviceId}>
                <Skeleton active title={false} paragraph={{ rows: 1, width: 270 }} loading={isLoading}>
                  <TestStatusCard
                    title={compressor.compressorName}
                    subTitle={compressor.modelName}
                    id={compressor.deviceId.toString()}
                    connectivityIconAndStatus={connectivityIconAndStatus}
                    LOAD={compressor.LOAD}
                    UNLOAD={compressor.UNLOAD}
                    testState={testState}
                    isNoPowerSensorVisible={isNoPowerSensorVisible(compressor)}
                  />
                </Skeleton>
              </div>
            ))}
          </CardsRow>
        </CardsContainer>
      </div>

      {isMobile ? (
        <MobileCommentDrawer
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleCommentSave}
          title={`${sectionTitle}`}
          flowStatus={flowSumStatus?.status}
          initialFlowComment={flowSumComment ?? ''}
          isSubmitted={testState === TestState.SUBMITTED}
        />
      ) : (
        <CommentsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleCommentSave}
          title={`${sectionTitle}`}
          flowStatus={flowSumStatus?.status}
          initialFlowComment={flowSumComment ?? ''}
          isSubmitted={testState === TestState.SUBMITTED}
        />
      )}
    </FlowGroupContainer>
  );
};

export default FlowSection;
